/**
 * Direct S3 Upload Utility
 * 
 * Uses presigned URLs to upload files directly to S3, bypassing
 * backend/ingress body size limits. This is the recommended approach
 * for all file uploads in production.
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/config/api';

// Get API URL dynamically at runtime to avoid stale build-time values
const API = getApiBaseUrl();

/**
 * Upload a file directly to S3 using presigned URLs with retry logic
 * 
 * @param {File} file - The file to upload
 * @param {string} projectId - The project ID
 * @param {Object} options - Additional options
 * @param {string} options.type - Asset type (background, character, video, voice, music, sfx, prop)
 * @param {string} options.name - Asset name (defaults to filename)
 * @param {string} options.folder - Optional folder/group name
 * @param {string} options.characterProfileId - Optional character profile ID for character assets
 * @param {Function} options.onProgress - Optional progress callback (0-100)
 * @param {number} options.retries - Number of retries on failure (default: 3)
 * @returns {Promise<Object>} The created asset record
 */
export async function uploadFileToS3(file, projectId, options = {}) {
  const {
    type = 'background',
    name = file.name,
    folder = null,
    characterProfileId = null,
    onProgress = null,
    retries = 3
  } = options;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Step 1: Get presigned URL for upload
      const presignedResponse = await axios.post(`${API}/api/uploads/get-presigned-url`, {
        filename: file.name,
        content_type: file.type,
        project_id: projectId
      });

      const { upload_url, s3_key } = presignedResponse.data;

      // Step 2: Upload directly to S3
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        } : undefined
      });

      // Step 3: Create asset record in database (skip_thumbnail for fast uploads)
      const assetPayload = {
        name,
        type,
        s3_key,
        folder,
        skip_thumbnail: true  // Skip thumbnail generation to prevent 520 timeouts
      };

      if (characterProfileId) {
        assetPayload.character_profile_id = characterProfileId;
      }

      const assetResponse = await axios.post(
        `${API}/api/projects/${projectId}/assets/from-s3`,
        assetPayload
      );

      return assetResponse.data;
    } catch (error) {
      console.warn(`Upload attempt ${attempt}/${retries} failed for ${file.name}:`, error.message);
      if (attempt < retries) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      } else {
        throw error;
      }
    }
  }
}

/**
 * Upload multiple files to S3 with batching to prevent overwhelming the server
 * 
 * @param {File[]} files - Array of files to upload
 * @param {string} projectId - The project ID
 * @param {Object} options - Options (same as uploadFileToS3, plus batch options)
 * @param {Function} options.onFileComplete - Called after each file completes (index, total, asset)
 * @param {Function} options.onError - Called on error (file, error)
 * @param {number} options.batchSize - Number of concurrent uploads (default: 3)
 * @returns {Promise<Object[]>} Array of created asset records
 */
export async function uploadFilesToS3(files, projectId, options = {}) {
  const {
    onFileComplete = null,
    onError = null,
    batchSize = 3,
    ...fileOptions
  } = options;

  const results = [];
  
  // Process in batches to avoid overwhelming the server
  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize);
    const batchPromises = batch.map((file, batchIndex) => {
      const fileIndex = i + batchIndex;
      return uploadFileToS3(file, projectId, {
        ...fileOptions,
        name: fileOptions.name || file.name
      })
        .then(asset => {
          results.push(asset);
          if (onFileComplete) {
            onFileComplete(fileIndex, files.length, asset);
          }
          return asset;
        })
        .catch(error => {
          console.error(`Failed to upload ${file.name}:`, error);
          if (onError) {
            onError(file, error);
          }
          return null;
        });
    });
    
    await Promise.all(batchPromises);
    
    // Small delay between batches
    if (i + batchSize < files.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }

  const successfulUploads = results.filter(r => r !== null);
  
  // Trigger background thumbnail generation after all uploads complete
  if (successfulUploads.length > 0) {
    try {
      await axios.post(`${API}/api/projects/${projectId}/generate-thumbnails`, {});
      console.log('Thumbnail generation started in background');
    } catch (error) {
      console.warn('Failed to start thumbnail generation:', error);
    }
  }

  return successfulUploads;
}

/**
 * Upload a Blob (e.g., recorded audio, canvas capture) to S3
 * 
 * @param {Blob} blob - The blob to upload
 * @param {string} projectId - The project ID
 * @param {string} filename - Filename to use
 * @param {Object} options - Same options as uploadFileToS3
 * @returns {Promise<Object>} The created asset record
 */
export async function uploadBlobToS3(blob, projectId, filename, options = {}) {
  // Convert Blob to File
  const file = new File([blob], filename, { type: blob.type });
  return uploadFileToS3(file, projectId, options);
}

/**
 * Get fresh presigned URLs for an existing asset (when URLs expire)
 * 
 * @param {string} assetId - The asset ID
 * @returns {Promise<Object>} Object with file_url and thumbnail_url
 */
export async function refreshAssetUrls(assetId) {
  const response = await axios.get(`${API}/api/assets/${assetId}/signed-urls`);
  return response.data;
}

export default {
  uploadFileToS3,
  uploadFilesToS3,
  uploadBlobToS3,
  refreshAssetUrls
};
